/**
 * Print service — turns a print job into a hidden iframe and triggers
 * the system print dialog.
 *
 * Two payload shapes, picked by the caller:
 *   - `html`     → injected via `srcdoc`, used for image and HTML jobs
 *   - `pdf-blob` → set as iframe `src`, used for PDF jobs (we generate
 *                  the bytes via pdf-lib so vector text stays sharp)
 */

import { marginCss, type PrintJob, type PrintSettings, paperCss } from "./print-types.js";

export type PrintPayload = { kind: "html"; html: string } | { kind: "pdf-blob"; blobUrl: string };

/**
 * Compose print-ready HTML for image/html jobs. PDF jobs go through
 * `recomposePdf` + `runPrint({kind:"pdf-blob"})` instead.
 */
export function buildPrintHtml(args: {
  title: string;
  job: Exclude<PrintJob, { kind: "pdf" }>;
  settings: PrintSettings;
}): string {
  const { title, job, settings } = args;
  // `@page { margin: 0 }` removes the space the browser would otherwise
  // fill with its own header/footer (URL, date, page x/y). The user's
  // chosen margin is applied as inner CSS padding instead.
  const pageRule = `@page { size: ${paperCss(settings.paper, settings.orientation)}; margin: 0; }`;
  const innerMargin = marginCss(settings.margin);
  const filter = settings.grayscale ? "filter: grayscale(1);" : "";
  const scale = Math.max(10, Math.min(400, settings.scalePct)) / 100;

  let body = "";
  if (job.kind === "image") {
    body = `<div class="page"><img src="${escapeAttr(job.src)}" alt="" /></div>`;
  } else {
    body = `<div class="page html">${job.html}</div>`;
  }

  return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  ${pageRule}
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page {
    box-sizing: border-box;
    width: 100%;
    height: 100vh;
    padding: ${innerMargin};
    display: flex;
    align-items: center;
    justify-content: center;
    page-break-after: always;
    break-after: page;
    overflow: hidden;
  }
  .page:last-child { page-break-after: auto; break-after: auto; }
  .page img {
    max-width: ${scale * 100}%;
    max-height: ${scale * 100}%;
    object-fit: contain;
    ${filter}
  }
  .page.html {
    align-items: flex-start;
    justify-content: flex-start;
    padding: ${innerMargin};
    ${filter}
  }
</style>
</head>
<body>${body}</body>
</html>`;
}

/**
 * Spawn a hidden iframe, load the payload, trigger print, clean up.
 *
 * Resolves once the print dialog has been requested (the browser does
 * not surface dismissal). The iframe is removed after a generous
 * timeout so cancelled jobs don't leak DOM nodes.
 */
export function runPrint(payload: PrintPayload): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";

    let settled = false;
    frame.onload = () => {
      // Give renderers a tick to fully decode (Safari fires print() too
      // early otherwise and prints a blank page on first run).
      window.setTimeout(() => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
          settled = true;
          resolve();
        } catch (e) {
          settled = true;
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      }, 80);
    };
    frame.onerror = () => {
      if (!settled) reject(new Error("Druck-iframe konnte nicht geladen werden."));
    };

    if (payload.kind === "html") {
      // srcdoc keeps everything same-origin so `contentWindow.print()`
      // works in Safari without cross-origin restrictions.
      frame.srcdoc = payload.html;
    } else {
      frame.src = payload.blobUrl;
    }
    document.body.appendChild(frame);
    // Clean up after the print dialog has had time to be dismissed.
    window.setTimeout(() => frame.remove(), 120_000);
  });
}

/* ─────────────────────── helpers ─────────────────────── */

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}
